import { reactive, ref } from 'vue';
import type { AfterMarketRow, AfterMarketTableActionEvent } from '../types';

const todayISO = (): string => {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

export const createAfterMarketTableStore = (initialRows: AfterMarketRow[]) => {
  const rows = reactive<AfterMarketRow[]>(initialRows.map((r) => ({ ...r })));
  const status = ref<'idle' | 'editing' | 'saving'>('idle');
  const newRowDraft = reactive<{ text: string; date: string }>({
    text: '',
    date: todayISO(),
  });
  let snapshot: AfterMarketRow[] | null = null;

  const dispatch = (
    event: AfterMarketTableActionEvent,
  ): AfterMarketRow[] | null => {
    switch (event.type) {
      case 'start-edit': {
        snapshot = rows.map((r) => ({ ...r }));
        status.value = 'editing';
        return null;
      }

      case 'request-save': {
        status.value = 'saving';
        return [...rows.map((r) => ({ ...r }))];
      }

      case 'complete-save': {
        snapshot = null;
        status.value = 'idle';
        return null;
      }

      case 'revert-save': {
        status.value = 'editing';
        return null;
      }

      case 'cancel': {
        if (snapshot) {
          rows.splice(0, rows.length, ...snapshot.map((r) => ({ ...r })));
        }
        snapshot = null;
        status.value = 'idle';
        newRowDraft.text = '';
        newRowDraft.date = todayISO();
        return null;
      }

      case 'add-row': {
        rows.push({
          id: crypto.randomUUID(),
          text: newRowDraft.text,
          date: newRowDraft.date,
        });
        newRowDraft.text = '';
        newRowDraft.date = todayISO();
        return null;
      }

      case 'set-new-row-field': {
        newRowDraft[event.field] = event.value;
        return null;
      }

      case 'delete-row': {
        const idx = rows.findIndex((r) => r.id === event.id);
        if (idx !== -1) rows.splice(idx, 1);
        return null;
      }

      case 'update-row': {
        const row = rows.find((r) => r.id === event.id);
        if (row) {
          (row as Record<string, string>)[event.field] = event.value;
        }
        return null;
      }
    }
  };

  const reset = (newRows: AfterMarketRow[]): void => {
    rows.splice(0, rows.length, ...newRows.map((r) => ({ ...r })));
    status.value = 'idle';
    newRowDraft.text = '';
    newRowDraft.date = todayISO();
  };

  return { rows, status, newRowDraft, dispatch, reset };
};
